import { motion } from "framer-motion";
import { Trash2, ShieldCheck, Share2, Clock, FileSignature, Send, MessageCircle, Image as ImageIcon, Eye, CheckCircle2, XCircle, FileText } from "lucide-react";
import { StatusPill } from "@/components/ui-helpers";
import { ReadinessRing } from "./ReadinessRing";

const HIDDEN_DATA_KEYS = ["notes", "description", "inclusions", "timeline", "share_token", "readiness_token", "alerted_milestones", "source_record_id", "channels", "alerted", "poster_image", "image", "viewed_at", "responded_at"];

const CONVERSION = {
  draft: { label: "Draft", cls: "bg-slate-100 text-slate-500", icon: FileSignature },
  sent: { label: "Sent to client", cls: "bg-blue-50 text-blue-600", icon: Send },
  viewed: { label: "Viewed by client", cls: "bg-amber-50 text-amber-600", icon: Eye },
  accepted: { label: "Accepted", cls: "bg-emerald-50 text-emerald-600", icon: CheckCircle2 },
  rejected: { label: "Declined", cls: "bg-rose-50 text-rose-500", icon: XCircle },
};

export const ResourceCard = ({ it, cfg, kk, index, rd, onTimeline, onReadiness, onQuoteFrom, onQuoteSend, onShare, onDelete, onToggleChannel, onWaReminder, onSnooze, onQuotePoster, onQuoteOptions, onQuotePdf }) => {
  const REMINDER = cfg.type === "reminder";
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}
      className="card-soft card-hover group relative p-4" data-testid={`${kk}-item`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-slate-800">{it.title}</h3>
          {it.subtitle && <p className="truncate text-sm text-slate-500">{it.subtitle}</p>}
        </div>
        <div className="flex items-center gap-1">
          {cfg.timeline && (
            <button onClick={() => onTimeline(it)} data-testid={`timeline-${kk}-item`} title="View timeline"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-sky-50 hover:text-sky-500"><Clock className="h-4 w-4" /></button>
          )}
          {cfg.readiness && (
            <button onClick={() => onReadiness(it)} data-testid={`readiness-${kk}-item`} title="Trip readiness"
              className="rounded-lg p-1 transition hover:bg-slate-50"><ReadinessRing r={rd} /></button>
          )}
          {cfg.quoteFrom && (
            <button onClick={() => onQuoteFrom(it)} data-testid={`quote-from-${kk}-item`} title="Create quotation from this rate"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-purple-50 hover:text-purple-500"><FileSignature className="h-4 w-4" /></button>
          )}
          {cfg.quoteSend && (
            <button onClick={() => onQuoteSend(it)} data-testid={`quote-send-${kk}-item`} title="Send via Email + WhatsApp"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-emerald-50 hover:text-emerald-500"><Send className="h-4 w-4" /></button>
          )}
          {cfg.quotePoster && (
            <button onClick={() => onQuotePoster(it)} data-testid={`quote-poster-${kk}-item`} title="Brand with a poster"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-purple-50 hover:text-purple-500">
              {it.data?.poster_image ? <ImageIcon className="h-4 w-4 text-purple-500" /> : <ImageIcon className="h-4 w-4" />}
            </button>
          )}
          {cfg.quoteOptions && (
            <button onClick={() => onQuoteOptions(it)} data-testid={`quote-options-${kk}-item`} title="Add Economy / Best Value / Premium options"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-indigo-50 hover:text-indigo-500"><FileSignature className="h-4 w-4" /></button>
          )}
          {cfg.quotePdf && (
            <button onClick={() => onQuotePdf(it)} data-testid={`quote-pdf-${kk}-item`} title="Download branded PDF"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-rose-50 hover:text-rose-500"><FileText className="h-4 w-4" /></button>
          )}
          {cfg.shareable && (
            <button onClick={() => onShare(it)} data-testid={`share-${kk}-item`} title="Copy share link"
              className="rounded-lg p-1.5 text-slate-300 transition hover:bg-blue-50 hover:text-blue-500 group-hover:opacity-100">
              <Share2 className="h-4 w-4" />
            </button>
          )}
          <button onClick={() => onDelete(it.record_id)} data-testid={`delete-${kk}-item`}
            className="rounded-lg p-1.5 text-slate-300 opacity-0 transition hover:bg-rose-50 hover:text-rose-500 group-hover:opacity-100">
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {it.status && <StatusPill status={it.status} />}
        {cfg.conversion && CONVERSION[it.status] && (() => {
          const m = CONVERSION[it.status];
          const when = (it.status === "accepted" || it.status === "rejected") ? it.data?.responded_at : it.status === "viewed" ? it.data?.viewed_at : null;
          return (
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${m.cls}`} data-testid={`${kk}-conversion`} title={when ? new Date(when).toLocaleString("en-GB") : ""}>
              <m.icon className="h-3 w-3" /> {m.label}{when ? ` · ${new Date(when).toLocaleDateString("en-GB")}` : ""}
            </span>
          );
        })()}
        {it.source_count > 1 ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-600" data-testid={`${kk}-sources`}
            title={(it.sources || []).map((s) => s.source).join(" · ")}>
            <ShieldCheck className="h-3 w-3" /> Confirmed by {it.source_count} sources · {(it.sources || []).map((s) => s.source).join(" + ")}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-0.5 text-[10px] text-slate-400">
            <ShieldCheck className="h-3 w-3" /> {it.source || "Manual"}
          </span>
        )}
      </div>
      {it.data && Object.keys(it.data).filter((k) => k !== "status" && it.data[k]).length > 0 && (
        <div className="mt-3 space-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
          {Object.entries(it.data).filter(([k, v]) => k !== "status" && v && typeof v !== "object" && !HIDDEN_DATA_KEYS.includes(k)).slice(0, 4).map(([k, v]) => (
            <div key={k} className="flex justify-between gap-2">
              <span className="capitalize text-slate-400">{k.replace(/_/g, " ")}</span>
              <span className="truncate font-medium text-slate-600">{/price|cost/i.test(k) && !isNaN(Number(v)) ? `₹${Number(v).toLocaleString("en-IN")}` : /^\d{4}-\d{2}-\d{2}/.test(String(v)) ? String(v).slice(0, 10).split("-").reverse().join("/") : String(v)}</span>
            </div>
          ))}
        </div>
      )}
      {REMINDER && (
        <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-3" data-testid="reminder-channels">
          <span className="text-[10px] font-medium text-slate-400">Notify via</span>
          {["email", "whatsapp"].map((ch) => {
            const on = ((it.data?.channels) || ["in_app", "email"]).includes(ch);
            return (
              <button key={ch} onClick={() => onToggleChannel(it, ch)} data-testid={`channel-${ch}-btn`}
                title={ch === "whatsapp" ? "We'll prepare a WhatsApp message you can send" : "Get an email when this is due"}
                className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize transition ${on ? "bg-blue-500 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{ch}</button>
            );
          })}
          <button onClick={() => onWaReminder(it)} data-testid="reminder-wa-btn"
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-200 px-2 py-0.5 text-[10px] font-medium text-emerald-600 hover:bg-emerald-50"><MessageCircle className="h-3 w-3" /> Send now</button>
          <button onClick={() => onSnooze(it)} data-testid="reminder-snooze-btn"
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-500 hover:bg-slate-50"><Clock className="h-3 w-3" /> Snooze 7d</button>
        </div>
      )}
    </motion.div>
  );
};
